import React, { useCallback, useContext, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { STEP_CONFIRM, STEP_SELECT_TYPE } from '@constants/process-steps';
import { PROCESS_IMPORT_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { ImportFormData } from '@hooks/import/type';
import { useProcessForm } from '@hooks/use-process-form/use-process-form';
import { UseProcessFormReturn } from '@hooks/use-process-form/use-process-form.types';
import { IMPORT_TYPE_CSV, PENDING_IMPORT_ID } from '@pages/events/utils/import-process-utils';

import { ImportCsvSteps } from './import-csv-steps';
import { ImportCSVQrCodeIssues, ProcessImportCSVStep, SPLITTER_COMMA } from './process-import-csv.type';

/*
 * ProcessImportCsv Component
 *
 * Handles the CSV import process for invitations and other data.
 * Navigates immediately to the progress page, which handles the mutation.
 */

export const ProcessImportCsv = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const { setPreventExit, toggleModal } = useContext(ModalContext);

  const { methods, offer, eventId }: UseProcessFormReturn = useProcessForm({
    additionalDefaults: {
      splitter: SPLITTER_COMMA,
    },
  });

  const {
    clubState: { slug: clubSlug },
    companyState: { legacyId: companyLegacyId },
  } = useContext(CompanyClubContext);

  const [step, setStep] = useState<ProcessImportCSVStep>(STEP_SELECT_TYPE);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<Array<string[]>>([]);
  const [localQrCodeIssues, setLocalQrCodeIssues] = useState<ImportCSVQrCodeIssues>({
    duplicateRows: [],
    emptyRows: [],
    invalidRows: [],
  });
  const [localFormData, setLocalFormData] = useState<ImportFormData>();

  const closeModalAndNavigate = useCallback(
    (formData: ImportFormData) => {
      if (!eventId) return;
      const path = PROCESS_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId)
        .replace(':importId', PENDING_IMPORT_ID)
        .replace(':importType', IMPORT_TYPE_CSV);

      setPreventExit(false);
      toggleModal({ isOpen: false, type: '' });
      navigate(path, { state: { formData } });
    },
    [eventId, companyLegacyId, clubSlug, setPreventExit, toggleModal, navigate]
  );

  const handleContinueReviewImport = (qrCodeIssues: ImportCSVQrCodeIssues, formData: ImportFormData) => {
    setLocalQrCodeIssues(qrCodeIssues);
    setLocalFormData(formData);
    if (qrCodeIssues.duplicateRows.length || qrCodeIssues.emptyRows.length || qrCodeIssues.invalidRows.length) {
      setStep(STEP_CONFIRM);
    } else {
      closeModalAndNavigate(formData);
    }
  };

  const handleContinueConfirmImportCsv = () => {
    if (localFormData) closeModalAndNavigate(localFormData);
  };

  const closeModal = () => {
    setPreventExit(false);
    toggleModal({ isOpen: false, type: '' });
  };

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      <FormProvider {...methods}>
        <ImportCsvSteps
          step={step}
          setStep={setStep}
          offer={offer}
          fileName={fileName}
          rows={rows}
          setRows={setRows}
          setFileName={setFileName}
          localQrCodeIssues={localQrCodeIssues}
          onContinueReviewImport={handleContinueReviewImport}
          onContinueConfirmImport={handleContinueConfirmImportCsv}
          onClose={closeModal}
        />
      </FormProvider>
    </form>
  );
};
